import { PermissionKey } from 'src/types/permissions.types';
import { resolveRouteAccess } from './routeAccess';

export interface RouteRequirement {
  /** Action permission required to open this route. */
  permission?: PermissionKey;
  /** Resource this route reads. Any scope above NONE passes. */
  viewResource?: string;
}

/**
 * What each route requires, keyed by full path. A route absent from this
 * map needs no permission beyond entering its section.
 */
export const ROUTE_REQUIREMENTS: Record<string, RouteRequirement> = {
  // /admin
  '/admin/teachers': { permission: 'teachers.view_all' },
  '/admin/add-teacher': { permission: 'teachers.manage' },
  '/admin/edit-teacher/:teacherId': { permission: 'teachers.manage' },
  '/admin/view-teacher/:teacherId': { permission: 'teachers.view_all' },
  '/admin/manage-transaction-types': { permission: 'transaction_types.manage' },
  '/admin/add-transaction-type': { permission: 'transaction_types.manage' },
  '/admin/edit-transaction-type/:transactionTypeId': { permission: 'transaction_types.manage' },
  '/admin/transaction-status-tracker': { viewResource: 'transactions' },
  '/admin/new-loan-request': { permission: 'loans.approve' },
  '/admin/transactions': { viewResource: 'transactions' },
  '/admin/transactions/:studentId': { viewResource: 'transactions' },
  '/admin/financial-simulations': { viewResource: 'whammies' },
  '/admin/view-financial-simulations/:id': { viewResource: 'whammies' },
  '/admin/add-scenario': { permission: 'whammies.create' },
  '/admin/edit-scenario/:scenarioId': { permission: 'whammies.edit' },
  '/admin/payroll': { permission: 'payroll.access' },
  '/admin/payroll/import': { permission: 'payroll.access' },
  '/admin/payroll/imports/:importId': { permission: 'payroll.access' },
  '/admin/payroll/roster': { permission: 'payroll.access' },
  '/admin/payroll/tax-day': { permission: 'payroll.access' },
  '/admin/cohorts': { viewResource: 'cohorts' },
  '/admin/cohorts/:cohortId': { viewResource: 'cohorts' },
  '/admin/roles': { permission: 'roles.manage' },
  '/admin/roles/:roleName': { permission: 'roles.manage' },
  // /teacher
  '/teacher/transaction-management': { permission: 'transactions.create' },
  '/teacher/students': { viewResource: 'students' },
  '/teacher/add-student': { permission: 'students.create' },
  '/teacher/edit-student/:studentId': { permission: 'students.edit' },
  '/teacher/view-student/:studentId': { viewResource: 'students' },
  '/teacher/loan-requests': { permission: 'loans.approve' },
  '/teacher/view-loan/:loanId': { permission: 'loans.approve' },
  '/teacher/street-cents': { viewResource: 'transactions' },
  '/teacher/street-cents/:id': { viewResource: 'transactions' },
  '/teacher/view-transaction/:id': { viewResource: 'transactions' },
  '/teacher/enrolled-students/:id': { viewResource: 'students' },
  '/teacher/financial-simulations': { viewResource: 'whammies' },
  '/teacher/view-financial-simulations/:id': { viewResource: 'whammies' },
  '/teacher/add-scenario': { permission: 'whammies.create' },
  '/teacher/edit-scenario/:scenarioId': { permission: 'whammies.edit' },
  '/teacher/street-cred': { viewResource: 'street_cred' },
  '/teacher/add-student-behaviour': { permission: 'street_cred.manage' },
  '/teacher/add-student-behaviour/:id': { permission: 'street_cred.manage' },
  '/teacher/edit-student-behavior/:id': { permission: 'street_cred.manage' },
  '/teacher/add-behavior-type': { permission: 'street_cred.manage' },
  '/teacher/edit-behaviour-type/:behaviourId': { permission: 'street_cred.manage' },
  '/teacher/assign-new-chellenge': { permission: 'whammies.assign' },
  '/teacher/assign-new-chellenge/:id': { permission: 'whammies.assign' },
  '/teacher/cohorts': { viewResource: 'cohorts' },
  '/teacher/cohorts/:cohortId': { viewResource: 'cohorts' },
  // /student
  '/student/street-cents': { viewResource: 'transactions' },
  '/student/street-cents/:id': { viewResource: 'transactions' },
  '/student/view-transaction/:id': { viewResource: 'transactions' },
  '/student/financial-simulations': { viewResource: 'whammies' },
  '/student/street-cred': { viewResource: 'street_cred' },
  '/student/view-financial-simulations/:id': { viewResource: 'whammies' },
};

/** The requirement for one route, or an empty requirement when it has none. */
export function getRouteRequirement(section: string, path: string): RouteRequirement {
  return ROUTE_REQUIREMENTS[`${section}/${path}`] || {};
}

/** Whether a signed-in role may open a route. Shares one decision path with the guard. */
export function canOpenRoute(role: string | undefined, section: string, path: string): boolean {
  const { permission, viewResource } = getRouteRequirement(section, path);
  const decision = resolveRouteAccess({
    isLoading: false,
    isAuthenticated: true,
    role,
    section,
    requiredPermission: permission,
    requiredViewResource: viewResource,
  });
  return decision.status === 'allow';
}
